import type { ConnectedSource } from '../types/platforms';

export const CONNECTED_SOURCES: ConnectedSource[] = [
  // Advertising Platforms
  { id: 'meta-ads', name: 'Meta Ads', iconClass: 'meta', iconLabel: 'M', logo: '/logos/meta_ads_logo.svg', status: 'disconnected', accountName: null, group: 'advertising', suggested: ['ecommerce', 'mobile', 'saas'] },
  { id: 'google-ads', name: 'Google Ads', iconClass: 'gads', iconLabel: 'G', logo: '/logos/google_ads_logo.svg', status: 'disconnected', accountName: null, group: 'advertising', suggested: ['ecommerce', 'saas', 'b2b'] },
  { id: 'tiktok-ads', name: 'TikTok Ads', iconClass: 'tiktok', iconLabel: 'T', logo: '/logos/tiktok_logo.svg', darkLogo: true, status: 'disconnected', accountName: null, group: 'advertising', suggested: ['ecommerce', 'mobile'] },
  { id: 'linkedin-ads', name: 'LinkedIn Ads', iconClass: 'linkedin', iconLabel: 'in', logo: '/logos/linkedIn_logo.svg', status: 'disconnected', accountName: null, group: 'advertising', suggested: ['b2b', 'saas'] },
  // Apple Search Ads. Status is read from the shared integration tables, so a
  // project connected in the main Vib3 app shows as connected here — but the
  // certificate is entered there, not in this product (see PlatformCard).
  // The logo is the platform-wide Apple Ads mark (same asset as the main Vib3
  // app), and darkLogo inverts the black glyph on the dark theme.
  //
  // 🔴 This is the ONLY Apple card. There used to be a second, `appstore` /
  // "App Store Connect", carrying a Coming soon badge and the same Apple glyph:
  // two Apple rows in one grid, one of them dead. Nothing connects it (it was
  // absent from the active-platform set in features/connect/activePlatforms),
  // so it offered the user a choice that did not exist. Re-add it only together
  // with a working connector.
  { id: 'apple-search-ads', name: 'Apple Ads', iconClass: 'appstore', iconLabel: 'AA', logo: '/logos/apple_ads_logo.svg', darkLogo: true, status: 'disconnected', accountName: null, group: 'advertising', suggested: ['mobile'] },

  // Analytics & Tracking
  { id: 'ga', name: 'Google Analytics 4', iconClass: 'ga', iconLabel: 'GA', logo: '/logos/google_analytics_logo.svg', status: 'disconnected', accountName: null, group: 'analytics', suggested: ['ecommerce', 'saas', 'b2b', 'mobile'] },

  // Revenue & Subscriptions
  { id: 'revenuecat', name: 'RevenueCat', iconClass: 'revenuecat', iconLabel: 'RC', logo: '/logos/revenuecat_logo.jpeg', status: 'disconnected', accountName: null, group: 'revenue', suggested: ['mobile', 'saas'] },
  { id: 'shopify', name: 'Shopify', iconClass: 'shopify', iconLabel: 'Sh', logo: '/logos/shopify_logo.svg', status: 'disconnected', accountName: null, group: 'revenue', suggested: ['ecommerce'] },

  // Mobile Attribution
  { id: 'appsflyer', name: 'AppsFlyer', iconClass: 'appsflyer', iconLabel: 'AF', logo: '/logos/appsflyer_logo.svg', status: 'disconnected', accountName: null, group: 'mobile', suggested: ['mobile'] },
];

export const GROUP_LABELS: Record<string, string> = {
  advertising: 'Advertising Platforms',
  analytics: 'Analytics & Tracking',
  crm: 'CRM & Email',
  revenue: 'Revenue & Subscriptions',
  mobile: 'Mobile Attribution',
  cloud: 'Data & Cloud',
};

const GROUP_ORDER_ALL = ['advertising', 'analytics', 'crm', 'revenue', 'mobile', 'cloud'] as const;

/**
 * The category filter's options — only groups that actually have a connector.
 * Derived rather than hand-listed: a group whose last connector is removed used
 * to stay in the dropdown as a filter that could only ever return nothing.
 */
export const GROUP_ORDER: string[] = GROUP_ORDER_ALL.filter(
  (group) => CONNECTED_SOURCES.some((source) => source.group === group),
);

/** One line under each connector's name in the grid. */
export const PLATFORM_DESCRIPTIONS: Record<string, string> = {
  'meta-ads': 'Ad spend, ROAS & campaigns',
  'google-ads': 'Search & display ad performance',
  'tiktok-ads': 'Video ad spend & conversions',
  'linkedin-ads': 'B2B ad campaigns & leads',
  ga: 'Traffic, conversions & behavior',
  revenuecat: 'In-app purchases & MRR',
  shopify: 'Orders, revenue & products',
  'apple-search-ads': 'App Store search ads, taps & installs',
  appsflyer: 'Attribution & install data',
  appstore: 'App downloads & ratings',
};

/**
 * Frontend platform id → backend provider name.
 *
 * Single source of truth: the inverse map and the fetch-tool map below are
 * derived from it, so a new connector is added in exactly one place. (This
 * used to be two hand-maintained inverse objects in PlatformCard and
 * ConnectView, which is how they drifted.)
 */
export const PLATFORM_TO_PROVIDER: Record<string, string> = {
  'meta-ads': 'meta',
  'tiktok-ads': 'tiktok',
  'google-ads': 'google_ads',
  ga: 'ga4',
  revenuecat: 'revenuecat',
  bigquery: 'bigquery',
  appsflyer: 'appsflyer',
  shopify: 'shopify',
  'linkedin-ads': 'linkedin',
  'apple-search-ads': 'apple_search_ads',
};

export const PROVIDER_TO_PLATFORM: Record<string, string> = Object.fromEntries(
  Object.entries(PLATFORM_TO_PROVIDER).map(([platform, provider]) => [provider, platform]),
);

/**
 * Provider → the agent tool that fetches its data. Used by the Collect step to
 * turn `tool_start` / `tool_result` events back into the right progress row.
 * BigQuery is absent on purpose: it has no fetcher, it's queried on demand.
 */
export const PROVIDER_FETCH_TOOL: Record<string, string> = {
  meta: 'fetch_meta_ads',
  tiktok: 'fetch_tiktok_ads',
  google_ads: 'fetch_google_ads',
  ga4: 'fetch_ga4_data',
  revenuecat: 'fetch_revenuecat_data',
  appsflyer: 'fetch_appsflyer_data',
  shopify: 'fetch_shopify_data',
  // Without this the Collect screen built ZERO fetcher rows for a
  // LinkedIn-only signup, and the empty-list guard in CollectView skipped the
  // whole pass — so the web-research step never ran either.
  linkedin: 'fetch_linkedin_ads',
  // Same reason as LinkedIn above: an Apple-only signup would build no
  // fetcher row at all, and CollectView's empty-list guard would skip the pass.
  apple_search_ads: 'fetch_apple_search_ads',
};

/** The industry chips above the connector grid, matched against `suggested`. */
export const INDUSTRY_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'ecommerce', label: 'E-commerce' },
  { id: 'b2b', label: 'B2B' },
  { id: 'saas', label: 'SaaS / Subscriptions' },
  { id: 'mobile', label: 'Mobile Apps' },
] as const;
